import type { RaceCategory, RacePhase } from "../api/types";

/** Whole seconds as m:ss, or h:mm:ss past an hour. */
export function formatRaceTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return "—";
  const totalSeconds = Math.round(ms / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** Gap to the leader in the live race state (seconds). */
export function formatGap(seconds: number | null | undefined, position?: number | null): string {
  if (position === 1) return "—";
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return "—";
  }
  if (seconds >= 60) return `+${formatRaceTime(seconds * 1000)}`;
  return `+${seconds.toFixed(1)}s`;
}

/** Gap from the results payload (milliseconds). */
export function formatGapMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms <= 0) return "—";
  if (ms >= 60_000) return `+${formatRaceTime(ms)}`;
  return `+${(ms / 1000).toFixed(1)}s`;
}

/** Countdown to the start. `GO` once the start time has passed. */
export function formatCountdown(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms <= 0) return "GO";
  const totalSeconds = Math.ceil(ms / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function phaseLabel(phase: RacePhase): string {
  switch (phase) {
    case "scheduled":
      return "Scheduled";
    case "registration":
      return "Registration open";
    case "pen":
      return "In the pen";
    case "countdown":
      return "Starting";
    case "racing":
      return "Racing";
    case "finished":
      return "Finished";
    default:
      return "Race";
  }
}

export function categoryRule(category: RaceCategory): string {
  switch (category) {
    case "A":
      return "4.0+ W/kg";
    case "B":
      return "3.2–4.0 W/kg";
    case "C":
      return "2.5–3.2 W/kg";
    case "D":
      return "Under 2.5 W/kg";
    default:
      return "";
  }
}

export function formatStartAt(startAt: number): string {
  if (!Number.isFinite(startAt) || startAt <= 0) return "Start time TBD";
  return new Date(startAt).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatStartIn(startAt: number, now = Date.now()): string {
  if (!Number.isFinite(startAt) || startAt <= 0) return "TBD";
  const delta = startAt - now;
  if (delta <= 0) return "Starting now";
  const minutes = Math.round(delta / 60_000);
  if (minutes < 60) return `in ${Math.max(1, minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours < 24) return remainder > 0 ? `in ${hours}h ${remainder}m` : `in ${hours}h`;
  const days = Math.round(hours / 24);
  return `in ${days}d`;
}

/** Registration is open only while the event is accepting entries. */
export function canRegisterNow(
  phase: RacePhase,
  registrationOpen: boolean,
  registered: number,
  capacity: number,
): boolean {
  if (!registrationOpen) return false;
  if (phase !== "registration") return false;
  return capacity <= 0 || registered < capacity;
}

/** Human entry slots, excluding the race bots that fill the field. */
export function humanCapacity(capacity: number, botCount: number): number {
  if (!Number.isFinite(capacity) || capacity <= 0) return 0;
  return Math.max(0, Math.round(capacity) - Math.max(0, Math.round(botCount)));
}

export function isRaceUpcoming(phase: RacePhase): boolean {
  return phase !== "finished";
}

export function sortRaces<T extends { startAt: number; phase: RacePhase }>(races: T[]): T[] {
  return [...races].sort((a, b) => {
    if (a.phase === "finished" && b.phase !== "finished") return 1;
    if (b.phase === "finished" && a.phase !== "finished") return -1;
    return a.startAt - b.startAt;
  });
}

export function nextRaceOf<T extends { startAt: number; phase: RacePhase }>(
  races: T[],
  now = Date.now(),
): T | null {
  const upcoming = sortRaces(races).filter((race) => race.phase !== "finished");
  return (
    upcoming.find((race) => race.startAt >= now) ??
    upcoming[0] ??
    null
  );
}
